import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const FAQs25 = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState('ordering');
    const [openFaq, setOpenFaq] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const categories = [
        { key: 'ordering', label: 'Ordering' },
        { key: 'shipping', label: 'Shipping' },
        { key: 'returns', label: 'Returns & Exchange' },
        { key: 'products', label: 'Products' },
        { key: 'account', label: 'Account' },
        { key: 'payment', label: 'Payment' },
    ];

    const faqs = {
        ordering: [
            { q: 'How do I place an order?', a: 'Browse our collection, select your desired product and size, add it to your cart, and proceed to checkout. You can pay using credit/debit cards, UPI, net banking, or cash on delivery for select locations.' },
            { q: 'Can I modify or cancel my order after placing it?', a: 'Orders can be modified or cancelled within 2 hours of placement. After this window, the order enters processing and cannot be changed. Please contact our support team immediately if you need to make changes.' },
            { q: 'Do you offer custom sizing?', a: 'Yes, we offer custom sizing on select products. Look for the "Custom Size" option on the product page. Custom orders typically take 2-3 additional business days for production.' },
            { q: 'Is there a minimum order value?', a: 'No, there is no minimum order value. However, orders above ₹2,000 qualify for free standard shipping.' },
        ],
        shipping: [
            { q: 'What are the shipping charges?', a: 'Standard shipping is free on orders above ₹2,000. For orders below ₹2,000, a flat fee of ₹199 applies. Express shipping is available at ₹399 for select PIN codes.' },
            { q: 'How long does delivery take?', a: 'Standard delivery takes 5-7 business days. Express delivery takes 2-3 business days. Metro cities typically receive orders within 3-5 business days. International orders take 10-15 business days.' },
            { q: 'Do you deliver internationally?', a: 'Yes, we ship to over 30 countries. International shipping rates are calculated at checkout based on destination and order weight. Customs duties may apply and are the responsibility of the buyer.' },
            { q: 'Can I track my order?', a: 'Absolutely. Once your order is shipped, you will receive a tracking number via email and SMS. You can also track your order from the "My Orders" section of your account.' },
        ],
        returns: [
            { q: 'What is your return policy?', a: 'We offer a 15-day return policy from the date of delivery. Items must be unworn, unwashed, and in original packaging with all tags attached. Please visit our Shipping & Exchange page for detailed information.' },
            { q: 'How do I initiate a return?', a: 'Contact us via email or WhatsApp with your order number and reason for return. Our team will review your request and provide a return authorization within 24 hours.' },
            { q: 'When will I receive my refund?', a: 'Refunds are processed within 7-10 business days after we receive and inspect the returned item. The amount will be credited to your original payment method. Bank processing may take an additional 3-5 days.' },
            { q: 'Can I exchange for a different size?', a: 'Yes, exchanges are available for a different size of the same product, subject to availability. The exchange process follows the same steps as returns. No additional shipping charges apply for exchanges.' },
        ],
        products: [
            { q: 'Are the product colors accurate?', a: 'We strive to display colors as accurately as possible. However, slight variations may occur due to screen settings, lighting conditions, and the handcrafted nature of our products. Each piece is unique.' },
            { q: 'How should I care for my garments?', a: 'Care instructions are provided on each product page and on the garment label. Generally, we recommend gentle hand wash or dry clean for silk and embroidered pieces. Cotton items can be machine washed on a delicate cycle.' },
            { q: 'Are your products handmade?', a: 'Many of our products feature handcrafted elements including hand weaving, block printing, and hand embroidery. Our product descriptions specify the craftsmanship involved in each piece.' },
            { q: 'How do I find the right size?', a: 'Each product page includes a detailed size chart with measurements. We recommend measuring yourself and comparing with our chart. If you are between sizes, we suggest going one size up for a comfortable fit.' },
        ],
        account: [
            { q: 'How do I create an account?', a: 'Click on the "Sign Up" button at the top of the page. Enter your name, email, phone number, and create a password. You will receive a verification email to confirm your account.' },
            { q: 'I forgot my password. What do I do?', a: 'Click "Forgot password?" on the login page. Enter your registered email address, and we will send you a password reset link. The link is valid for 24 hours.' },
            { q: 'Can I delete my account?', a: 'Yes, you can request account deletion by contacting our support team. Please note that this action is irreversible and you will lose your order history, wishlist, and saved addresses.' },
        ],
        payment: [
            { q: 'What payment methods do you accept?', a: 'We accept Visa, Mastercard, American Express, Rupay debit/credit cards, UPI (Google Pay, PhonePe, Paytm), net banking, and Cash on Delivery for select PIN codes.' },
            { q: 'Is my payment information secure?', a: 'Yes, all payment transactions are encrypted using 256-bit SSL technology. We do not store your complete card details on our servers. Our payment processing is handled by certified, PCI-DSS compliant payment gateways.' },
            { q: 'Do you offer EMI options?', a: 'Yes, EMI options are available on select credit cards for orders above ₹5,000. Available EMI plans are displayed during checkout.' },
            { q: 'Can I pay with multiple payment methods?', a: 'Currently, each order can only be paid with a single payment method. If you have store credit or a gift card, it can be combined with another payment method.' },
        ],
    };

    const toggleFaq = (idx) => {
        setOpenFaq(openFaq === idx ? null : idx);
    };

    const currentFaqs = faqs[activeCategory] || [];
    const filteredFaqs = searchQuery
        ? Object.values(faqs).flat().filter(f => f.q.toLowerCase().includes(searchQuery.toLowerCase()) || f.a.toLowerCase().includes(searchQuery.toLowerCase()))
        : currentFaqs;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-faqs * { box-sizing: border-box; }
                .sahaj-faqs { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-faqs-heading { font-family: 'Space Grotesk', sans-serif; }
                .sahaj-faqs input:focus { border-color: #0D7C66 !important; }
            `}</style>
            <div className="sahaj-faqs" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero */}
                <div style={{ background: '#111111', padding: '60px 0', marginTop: 80 }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h1 className="sahaj-faqs-heading" style={{
                            fontSize: 48, fontWeight: 700, color: '#FFFFFF', margin: 0, letterSpacing: '-1px'
                        }}>
                            FREQUENTLY ASKED QUESTIONS
                        </h1>
                        <p style={{ fontSize: 16, color: '#9CA3AF', marginTop: 12, fontWeight: 300 }}>
                            Find answers to common questions
                        </p>
                    </div>
                </div>

                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 24px 80px' }}>
                    {/* Search */}
                    <div style={{ maxWidth: 600, margin: '0 auto 48px', position: 'relative' }}>
                        <Search size={20} color="#6C757D" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            type="text"
                            placeholder="Search questions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '16px 16px 16px 48px',
                                border: '1px solid #DEE2E6', background: '#F8F9FA',
                                fontSize: 15, outline: 'none', color: '#111111',
                                fontFamily: 'IBM Plex Sans, sans-serif',
                                borderRadius: 0, transition: 'border-color 0.2s'
                            }}
                        />
                    </div>

                    {/* Category Tabs */}
                    {!searchQuery && (
                        <div style={{
                            display: 'flex', gap: 0, borderBottom: '2px solid #DEE2E6',
                            marginBottom: 40, overflowX: 'auto'
                        }}>
                            {categories.map(cat => (
                                <button
                                    key={cat.key}
                                    onClick={() => { setActiveCategory(cat.key); setOpenFaq(null); }}
                                    className="sahaj-faqs-heading"
                                    style={{
                                        padding: '14px 24px', border: 'none', background: 'none',
                                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                        textTransform: 'uppercase', letterSpacing: '1px',
                                        color: activeCategory === cat.key ? '#0D7C66' : '#6C757D',
                                        borderBottom: activeCategory === cat.key ? '2px solid #0D7C66' : '2px solid transparent',
                                        marginBottom: -2, transition: 'all 0.2s', whiteSpace: 'nowrap'
                                    }}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {searchQuery && (
                        <p style={{ fontSize: 14, color: '#6C757D', marginBottom: 24 }}>
                            Showing {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
                        </p>
                    )}

                    {/* FAQ Accordion */}
                    <div style={{ maxWidth: 800 }}>
                        {filteredFaqs.map((faq, idx) => (
                            <div
                                key={idx}
                                style={{
                                    borderBottom: '1px solid #DEE2E6',
                                    background: openFaq === idx ? '#F8F9FA' : '#FFFFFF',
                                    transition: 'background 0.2s'
                                }}
                            >
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    style={{
                                        width: '100%', display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'center', padding: '20px 24px', border: 'none',
                                        background: 'none', cursor: 'pointer', textAlign: 'left',
                                        fontFamily: 'IBM Plex Sans, sans-serif'
                                    }}
                                >
                                    <span className="sahaj-faqs-heading" style={{
                                        fontSize: 16, fontWeight: 600, color: openFaq === idx ? '#0D7C66' : '#111111',
                                        paddingRight: 16, transition: 'color 0.2s'
                                    }}>
                                        {faq.q}
                                    </span>
                                    <div style={{
                                        width: 32, height: 32, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', flexShrink: 0,
                                        background: openFaq === idx ? '#0D7C66' : '#F8F9FA',
                                        transition: 'all 0.2s'
                                    }}>
                                        {openFaq === idx
                                            ? <ChevronUp size={18} color="#FFFFFF" />
                                            : <ChevronDown size={18} color="#6C757D" />
                                        }
                                    </div>
                                </button>
                                {openFaq === idx && (
                                    <div style={{ padding: '0 24px 24px' }}>
                                        <p style={{
                                            fontSize: 15, lineHeight: 1.8, color: '#6C757D', margin: 0,
                                            borderLeft: '3px solid #0D7C66', paddingLeft: 20
                                        }}>
                                            {faq.a}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {filteredFaqs.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '60px 0' }}>
                            <p className="sahaj-faqs-heading" style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                                No results found
                            </p>
                            <p style={{ color: '#6C757D', marginBottom: 24 }}>Try a different search term</p>
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    padding: '10px 24px', background: '#0D7C66', color: '#FFFFFF',
                                    border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                    fontFamily: 'Space Grotesk, sans-serif',
                                    textTransform: 'uppercase', letterSpacing: '1px'
                                }}
                            >
                                Clear Search
                            </button>
                        </div>
                    )}

                    {/* Still Need Help */}
                    <div style={{
                        background: '#111111', padding: 48, marginTop: 60,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexWrap: 'wrap', gap: 20
                    }}>
                        <div>
                            <h3 className="sahaj-faqs-heading" style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px' }}>
                                Still have questions?
                            </h3>
                            <p style={{ fontSize: 15, color: '#9CA3AF', margin: 0 }}>
                                Our support team is here to help you
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/template25/contact')}
                            style={{
                                padding: '14px 32px', background: '#0D7C66', color: '#FFFFFF',
                                border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                                textTransform: 'uppercase', letterSpacing: '2px',
                                fontFamily: 'Space Grotesk, sans-serif',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.target.style.background = '#0A9B7F'}
                            onMouseLeave={e => e.target.style.background = '#0D7C66'}
                        >
                            Contact Us
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default FAQs25;
